import { useState } from "react";

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { addDestinationToTracker } from "helpers/destinations/createDestination";
import { addSourceToTracker } from "helpers/Sources/createSource";

import SwalAlertComp from "components/SwalAlert";

import {
    setPreSelectedDestinationIds,
    setPreSelectedSourceId,
} from "../../../redux/slices/wizardSourceDestination/wizardSourceDestination.slice";

/**
 * State and actions for the "attach to tracker" step shown after a source or
 * destination is created. The host renders `TrackerStepContent` with the
 * returned state and drives `addToTracker` / `createNewTracker` from its own
 * footer.
 */
const useTrackerStep = ({
    sourceId,
    destinationId,
    language,
    handleClose,
    handleGetSources,
    handleGetDestinations,
}: any) => {
    const [selectedTracker, setSelectedTracker] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const addToTracker = async () => {
        if (isLoading || !selectedTracker) {
            return false;
        }
        setIsLoading(true);

        if (destinationId) {
            const payload = { destinations: destinationId };
            const response = await addDestinationToTracker(
                payload,
                selectedTracker,
            );
            handleClose();
            setIsLoading(false);
            if (response.status) {
                handleGetDestinations && handleGetDestinations();
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message && response.message,
                    icon: "success",
                    timer: 3000,
                });
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
            }
            return response?.status;
        }

        const ids = Array.isArray(sourceId) ? sourceId : [sourceId];
        let allSucceeded = true;
        for (const id of ids) {
            const response = await addSourceToTracker(
                { sources: [id] },
                selectedTracker,
            );
            if (!response.status) {
                allSucceeded = false;
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
            }
        }
        handleClose();
        setIsLoading(false);
        if (allSucceeded) {
            handleGetSources && handleGetSources();
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: "Source(s) attached to tracker",
                icon: "success",
                timer: 3000,
            });
        }
        return allSucceeded;
    };

    const createNewTracker = () => {
        if (sourceId) {
            dispatch(setPreSelectedSourceId(sourceId));
        }
        if (destinationId) {
            const ids = Array.isArray(destinationId)
                ? destinationId
                : [destinationId];
            dispatch(setPreSelectedDestinationIds(ids));
        }
        handleClose();
        navigate("/wizard");
    };

    return {
        isDestination: Boolean(destinationId),
        selectedTracker,
        setSelectedTracker,
        isLoading,
        addToTracker,
        createNewTracker,
    };
};

export default useTrackerStep;
